import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  InputAdornment,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff,
  VpnKey as CredentialsIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon,
  PlaylistAddCheck as SelectModeIcon
} from '@mui/icons-material';
import InternalOrgUsersDialog from '../components/InternalOrgUsersDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../services/api.service';
import useThaklisServicesOff from '../hooks/useThaklisServicesOff';
import PortalCustomerGroups from './Admin/PortalCustomerGroups';
import OurCompanyTab from './organizations/OurCompanyTab';
import TradeLicenceSection, { TradeLicenceImport } from '../components/clients/TradeLicenceSection';
import {
  applyToClient, EMPTY_LICENCE, fromExtraction, toLicenceForm, licenceMetadata, saveClientWithLicence,
} from '../components/clients/tradeLicenceForm';
import { tradeLicenceService } from '../services/clients/tradeLicence.service';
import { tcOrgDocumentApi } from '../components/org-documents/orgDocumentApi';
import PortalAccessSection from '../components/clients/PortalAccessSection';
import { portalFeaturesService } from '../services/portal/portalFeatures.service';
import { PORTAL_FEATURES, OVERRIDE, overridesSummary } from '../services/portal/portalFeatures';
import { getBrandedTableHeadSx } from '../utils/colorUtils';
import { useColumnPreferences } from '../components/pro-table';
import ProTablePreferences from '../components/pro-table/ProTablePreferences';
import { isPlatformLevel, can } from '../utils/roleUtils';
import { serverMessage } from '../utils/apiConvert';

const CLIENT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'trn', label: 'TRN' },
  { key: 'outstanding', label: 'Outstanding', align: 'right' },
  { key: 'walletBalance', label: 'Wallet Balance', align: 'right' },
  { key: 'netLiability', label: 'Net Liability', align: 'right' },
  { key: 'invoices', label: 'Invoices', align: 'center' },
  { key: 'portal', label: 'Portal' },
];

const Organizations = ({ currentUser }) => {
  // Document expiry is a Thaklis feature — hide the portal doc-expiry toggle when Thaklis is disabled.
  const thaklisDisabled = currentUser?.thaklisEnabled === false;
  const thaklisServicesOff = useThaklisServicesOff(currentUser);
  const navigate = useNavigate();
  const theme = useTheme();

  // Role check helper
  const isPlatform = isPlatformLevel(currentUser?.role);
  const showCustomerGroupsTab = can(currentUser, 'customer_organization', 'read');
  const canReadInvoice = can(currentUser, 'invoice', 'read');
  const canReadFinance = can(currentUser, 'finance', 'read');

  // Selected tab by key ('CLIENT' | 'TYPING_CENTER' | 'groups' | 'company'); null = first available tab.
  // ?tab=company&view=details|branding — old Configuration links (company settings, branding) land here.
  const [urlParams, setUrlParams] = useSearchParams();
  const [selectedTabKey, setSelectedTabKey] = useState(urlParams.get('tab') || null);
  const companyView = urlParams.get('view');
  const setCompanyView = (v) => {
    const next = new URLSearchParams(urlParams);
    next.set('tab', 'company');
    if (v) next.set('view', v); else next.delete('view');
    setUrlParams(next, { replace: true });
  };

  const [selectedTypingCenter, setSelectedTypingCenter] = useState(
    isPlatform ? '' : currentUser?.typingCenterId
  );
  const [typingCenters, setTypingCenters] = useState([]);
  const [thaklisOrganizations, setThaklisOrganizations] = useState([]);
  const [takenOrgIds, setTakenOrgIds] = useState(new Set());
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMappingDialog, setOpenMappingDialog] = useState(false);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [orgTicketData, setOrgTicketData] = useState({});
  const [orgWalletData, setOrgWalletData] = useState({});

  // Organization Config Dialog State
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  // Bumped when the config dialog closes so the Our Company tab re-reads credential status.
  const [configClosedCount, setConfigClosedCount] = useState(0);
  const [configOrg, setConfigOrg] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [defaultEmails, setDefaultEmails] = useState('');
  const [defaultEmailsError, setDefaultEmailsError] = useState('');
  const [configNotes, setConfigNotes] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Config Dialog Tab State
  const [configTab, setConfigTab] = useState(0);

  // Credentials State
  const [credentialStatus, setCredentialStatus] = useState(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsSaving, setCredentialsSaving] = useState(false);
  const [credentialsTesting, setCredentialsTesting] = useState(false);
  const [credentialsForm, setCredentialsForm] = useState({ thaklisUsername: '', thaklisPassword: '' });
  const [showPassword, setShowPassword] = useState(false);

  const [typingCenterOrgType, setTypingCenterOrgType] = useState(null);
  // A B2B TC's "clients" are themselves typing centers — relabel the client tab/dialogs accordingly.
  const isB2b = typingCenterOrgType === 'B2B';
  const clientTerm = isB2b ? 'Typing Center' : 'Client';
  const clientTermPlural = isB2b ? 'Typing Centers' : 'Clients';

  // Per-customer type: a B2B may have both typing-center customers and (when Thaklis is enabled)
  // clients; every other TC has clients only. Null (old rows) derives from the parent TC.
  const effectiveOrgType = (o) => (o?.type) || (isB2b ? 'TYPING_CENTER' : 'CLIENT');
  const termFor = (typeKey) => (typeKey === 'TYPING_CENTER' ? 'Typing Center' : 'Client');
  const termPluralFor = (typeKey) => (typeKey === 'TYPING_CENTER' ? 'Typing Centers' : 'Clients');
  // The customer tabs this TC gets, in order: Typing Centers (B2B only), then Clients (any TC that
  // has Thaklis features, i.e. non-B2B or a Thaklis-enabled B2B). PRO TCs have no customers.
  const canHaveCustomers = ['TYPING_CENTER', 'B2B'].includes(typingCenterOrgType);
  const customerTabs = [
    ...(canHaveCustomers && isB2b ? ['TYPING_CENTER'] : []),
    ...(canHaveCustomers && (!isB2b || !thaklisDisabled) ? ['CLIENT'] : []),
  ];
  // The typing center's own linked account first, then its customers, then groups.
  const tabKeys = ['company', ...customerTabs, ...(showCustomerGroupsTab ? ['groups'] : [])];
  const activeTabKey = tabKeys.includes(selectedTabKey) ? selectedTabKey : tabKeys[0];
  const activeCustomerType = customerTabs.includes(activeTabKey) ? activeTabKey : null;

  const [internalOrganizations, setInternalOrganizations] = useState([]);
  const [internalOrgsLoading, setInternalOrgsLoading] = useState(false);
  // Server-side paging/search state for the active customer tab.
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(12);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerCounts, setCustomerCounts] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const clientColPrefs = useColumnPreferences('organizations.clients', CLIENT_COLUMNS);
  const [customerView, setCustomerView] = useState(() => {
    try { return localStorage.getItem('organizationsCustomerView') === 'table' ? 'table' : 'cards'; }
    catch (e) { return 'cards'; }
  });
  const [internalOrgsError, setInternalOrgsError] = useState(null);

  const [openInternalOrgDialog, setOpenInternalOrgDialog] = useState(false);
  const [internalOrgSaving, setInternalOrgSaving] = useState(false);
  const [editingInternalOrg, setEditingInternalOrg] = useState(null);

  // Remove-client dialog (with optional refund of the wallet balance to the customer from a TC bank)
  const [deleteDialog, setDeleteDialog] = useState({
    open: false, org: null, loading: false, submitting: false,
    walletBalance: 0, banks: [], refund: false, bankId: '', error: null
  });
  const [internalOrgFormData, setInternalOrgFormData] = useState({
    name: '',
    address: '',
    emirate: '',
    country: 'UAE',
    contactPerson: '',
    contactNumber: '',
    email: '',
    trnNumber: '',
    type: 'CLIENT'
  });

  // Portal feature switches (14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §4.2–4.3).
  const [portalDefaults, setPortalDefaults] = useState({});
  const [portalOverrides, setPortalOverrides] = useState({});
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false); // checkboxes hidden until the user starts a bulk portal change
  const exitSelectMode = () => { setSelectMode(false); setSelectedClientIds([]); };
  const [bulkPortal, setBulkPortal] = useState(null); // { feature, value, saving, error }
  const [portalVersion, setPortalVersion] = useState(0); // bump to re-read overrides after a bulk change
  useEffect(() => {
    portalFeaturesService.getDefaults().then((d) => setPortalDefaults(d || {})).catch(() => {});
  }, []);

  // Trade licence on the client dialog: an import only fills the form; the client save stores the file and the profile.
  const [licenceImport, setLicenceImport] = useState({ importing: false, error: null, warnings: [], imported: false });
  const [licenceSuggestions, setLicenceSuggestions] = useState({});
  const [clientDialogError, setClientDialogError] = useState(null);
  const [licence, setLicence] = useState(EMPTY_LICENCE);
  const [licenceOpen, setLicenceOpen] = useState(false);
  const licenceFile = useRef(null);
  const licencePending = useRef({ document: false, profile: false });

  const resetLicence = () => {
    setLicenceImport({ importing: false, error: null, warnings: [], imported: false });
    setLicenceSuggestions({});
    setClientDialogError(null);
    setLicence(EMPTY_LICENCE);
    setLicenceOpen(false);
    licenceFile.current = null;
    licencePending.current = { document: false, profile: false };
  };

  const handleImportLicence = async (file) => {
    setLicenceImport((p) => ({ ...p, importing: true, error: null, warnings: [] }));
    try {
      const extracted = await tradeLicenceService.extract(file);
      const { form, suggestions } = applyToClient(internalOrgFormData, extracted);
      setInternalOrgFormData(form);
      setLicenceSuggestions(suggestions);
      setLicence(fromExtraction(extracted));
      setLicenceOpen(true);
      licenceFile.current = file;
      licencePending.current = { document: true, profile: true };
      setLicenceImport({ importing: false, error: null, warnings: extracted?.warnings || [], imported: true });
    } catch (e) {
      setLicenceImport((p) => ({
        ...p, importing: false,
        error: e?.response?.data?.message || e?.response?.data?.error || "Couldn't read the licence. Try a clearer scan.",
      }));
    }
  };

  const SUGGESTION_LABELS = {
    name: 'Name', trnNumber: 'TRN', contactPerson: 'Contact person', contactNumber: 'Contact number',
    email: 'Email', emirate: 'Emirate', address: 'Address', country: 'Country',
  };

  const emirateOptions = [
    'Abu Dhabi',
    'Ajman',
    'Dubai',
    'Fujairah',
    'Ras Al Khaimah',
    'Sharjah',
    'Umm Al Quwain'
  ];

  // Portal Users Dialog state
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [selectedOrgForUsers, setSelectedOrgForUsers] = useState(null);

  // Financial data per internal org (keyed by org ID)
  const [orgFinancials, setOrgFinancials] = useState({});
  // Employee count per client org (keyed by org ID), CLIENT-type orgs only
  const [employeeCounts, setEmployeeCounts] = useState({});

  const handleOpenUsersDialog = (org) => {
    setSelectedOrgForUsers(org);
    setOpenUsersDialog(true);
  };

  const handleCloseUsersDialog = () => {
    setOpenUsersDialog(false);
    setSelectedOrgForUsers(null);
  };

  const resolvedTypingCenterId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;

  const normalizeInternalOrganization = useCallback((item) => {
    if (!item) return null;
    return {
      id: item.id,
      typingCenterId: item.typingCenterId ?? item.typing_center_id,
      name: item.name ?? '',
      address: item.address ?? '',
      emirate: item.emirate ?? '',
      country: item.country ?? '',
      contactPerson: item.contactPerson ?? item.contact_person ?? '',
      contactNumber: item.contactNumber ?? item.contact_number ?? '',
      email: item.email ?? '',
      trnNumber: item.trnNumber ?? item.trn_number ?? '',
      type: item.type ?? null,
      portalFeatureOverrides: item.portalFeatureOverrides ?? item.portal_feature_overrides ?? {}
    };
  }, []);

  // Platform users pick a typing center; default to the first real one
  // Organisations already mapped to any typing centre (an organisation can have only one mapping).
  const loadTakenOrgIds = useCallback(() => {
    if (!isPlatform) return;
    ApiService.get('/api/mappings')
      .then((response) => setTakenOrgIds(new Set((response.data?.data || []).map((m) => m.organization_id))))
      .catch((err) => console.error('❌ [Organizations] Error fetching mappings:', err));
  }, [isPlatform]);

  useEffect(() => {
    if (!isPlatform) return;
    ApiService.getTypingCenters()
      .then((response) => {
        const list = response.data?.data || [];
        setTypingCenters(list);
        setSelectedTypingCenter(prev => prev || list[0]?.id || '');
      })
      .catch((err) => console.error('❌ [Organizations] Error fetching typing centers:', err));
    ApiService.get('/api/organisations')
      .then((response) => setThaklisOrganizations(response.data?.data || []))
      .catch((err) => console.error('❌ [Organizations] Error fetching Thaklis organizations:', err));
    loadTakenOrgIds();
  }, [isPlatform, loadTakenOrgIds]);


  // Fetch mapped organizations
  const fetchMappedOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const centerId = resolvedTypingCenterId;

      if (!centerId) {
        setOrganizations([]);
        setLoading(false);
        return;
      }

      const response = await ApiService.get(`/api/typing-centers/${centerId}/with-mappings`);
      if (response.data && response.data.success) {
        const centerData = response.data.data;
        const orgType = centerData?.organization_type || centerData?.organizationType || null;
        setTypingCenterOrgType(orgType);
        const mappedOrgs = centerData.mapped_organizations || centerData.mappedOrganizations || [];
        console.log('📋 [Organizations] Fetched organizations:', mappedOrgs.length);
        setOrganizations(mappedOrgs);
      }
    } catch (err) {
      console.error('❌ [Organizations] Error fetching organizations:', err);
      setOrganizations([]);
      setTypingCenterOrgType(null);
    } finally {
      setLoading(false);
    }
  }, [resolvedTypingCenterId]);

  useEffect(() => {
    if (currentUser) {
      fetchMappedOrganizations();
    }
  }, [currentUser, fetchMappedOrganizations]);

  const fetchInternalOrganizations = useCallback(async () => {
    const centerId = resolvedTypingCenterId;
    // Internal orgs (clients) exist for every TC type except PRO — TYPING_CENTER and B2B both have them.
    if (!centerId || !['TYPING_CENTER', 'B2B'].includes(typingCenterOrgType)) {
      setInternalOrganizations([]);
      setCustomerTotal(0);
      setCustomerCounts({});
      return;
    }

    // No tab is open yet, but its badge still needs the counts every page response carries.
    const countsOnly = !activeCustomerType;

    try {
      if (!countsOnly) {
        setInternalOrgsLoading(true);
        setInternalOrgsError(null);
      }

      const response = await ApiService.getInternalOrganizationsPage({
        typingCenterId: centerId,
        ...(countsOnly ? {} : { type: activeCustomerType }),
        ...(countsOnly || !searchTerm ? {} : { search: searchTerm }),
        page: countsOnly ? 0 : customerPage,
        size: countsOnly ? 1 : customerRowsPerPage
      });

      const pageData = response.data?.data || {};
      const counts = pageData.counts || {};
      setCustomerCounts({
        CLIENT: Number(counts.clients ?? 0),
        TYPING_CENTER: Number(counts.typing_centers ?? counts.typingCenters ?? 0)
      });
      if (countsOnly) return;

      const list = Array.isArray(pageData.content) ? pageData.content : [];
      setInternalOrganizations(list.map(normalizeInternalOrganization).filter(Boolean));
      setCustomerTotal(pageData.total_elements ?? pageData.totalElements ?? list.length);
    } catch (err) {
      console.error('❌ [InternalOrganizations] Error fetching internal organizations:', err);
      setCustomerCounts({});
      if (countsOnly) return;
      setInternalOrganizations([]);
      setCustomerTotal(0);
      setInternalOrgsError(`Failed to load ${clientTermPlural.toLowerCase()}`);
    } finally {
      if (!countsOnly) setInternalOrgsLoading(false);
    }
  }, [normalizeInternalOrganization, resolvedTypingCenterId, typingCenterOrgType,
      activeCustomerType, searchTerm, customerPage, customerRowsPerPage]);

  useEffect(() => {
    fetchInternalOrganizations();
  }, [fetchInternalOrganizations]);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setCustomerPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setCustomerPage(0);
  }, [activeCustomerType]);

  // Financials for every customer in one pair of requests, not one pair per customer.
  useEffect(() => {
    const centerId = resolvedTypingCenterId;
    if (internalOrganizations.length === 0 || !centerId) return;

    const fetchFinancials = async () => {
      const orgIds = internalOrganizations.map((o) => o.id);
      const clientOrgIds = internalOrganizations.filter((o) => effectiveOrgType(o) === 'CLIENT').map((o) => o.id);
      const [summaryRes, walletsRes, employeeCountsRes] = await Promise.allSettled([
        canReadInvoice ? ApiService.getInvoiceOrgSummary(orgIds, centerId) : null,
        canReadFinance ? ApiService.getCustomerWalletBalances(orgIds, centerId) : null,
        clientOrgIds.length ? ApiService.getInternalOrgEmployeeCounts(clientOrgIds, centerId) : null
      ]);

      setEmployeeCounts(
        employeeCountsRes.status === 'fulfilled' && employeeCountsRes.value
          ? (employeeCountsRes.value?.data?.data || {})
          : {}
      );

      const summaryOk = summaryRes.status === 'fulfilled' && !!summaryRes.value;
      const walletsOk = walletsRes.status === 'fulfilled' && !!walletsRes.value;
      const summaries = summaryOk ? (summaryRes.value?.data?.data || []) : [];
      const wallets = walletsOk ? (walletsRes.value?.data?.data || []) : [];

      const walletByOrg = {};
      wallets.forEach((w) => {
        const orgId = w.internal_organization_id ?? w.internalOrganizationId;
        if (orgId) walletByOrg[orgId] = Number(w.approved_balance ?? w.approvedBalance ?? 0);
      });

      const summaryByOrg = {};
      summaries.forEach((s) => {
        const orgId = s.internal_organization_id ?? s.internalOrganizationId;
        if (orgId) summaryByOrg[orgId] = s;
      });

      setOrgFinancials(
        orgIds.reduce((acc, orgId) => {
          const s = summaryByOrg[orgId] || {};
          acc[orgId] = {
            outstanding: summaryOk ? Number(s.outstanding_amount ?? s.outstandingAmount ?? 0) : null,
            invoiced: summaryOk ? Number(s.invoiced_amount ?? s.invoicedAmount ?? 0) : null,
            collected: summaryOk ? Number(s.collected_amount ?? s.collectedAmount ?? 0) : null,
            walletBalance: walletsOk ? (walletByOrg[orgId] ?? 0) : null,
            finalCount: summaryOk ? Number(s.final_count ?? s.finalCount ?? 0) : null,
            draftCount: summaryOk ? Number(s.draft_count ?? s.draftCount ?? 0) : null
          };
          return acc;
        }, {})
      );
    };

    fetchFinancials().catch((e) => console.error('Error fetching customer financials', e));
  }, [internalOrganizations, resolvedTypingCenterId, canReadInvoice, canReadFinance]);

  const handleOpenCreateInternalOrg = (type = 'CLIENT') => {
    setEditingInternalOrg(null);
    setInternalOrgFormData({
      name: '',
      address: '',
      emirate: '',
      country: 'UAE',
      contactPerson: '',
      contactNumber: '',
      email: '',
      trnNumber: '',
      // Set by the tab the user created from (Typing Centers vs Clients); immutable afterwards.
      type
    });
    setPortalOverrides({});
    resetLicence();
    setOpenInternalOrgDialog(true);
  };

  const handleOpenEditInternalOrg = (org) => {
    const normalized = normalizeInternalOrganization(org);
    setEditingInternalOrg(normalized);
    setInternalOrgFormData({
      name: normalized?.name || '',
      address: normalized?.address || '',
      emirate: normalized?.emirate || '',
      country: normalized?.country || 'UAE',
      contactPerson: normalized?.contactPerson || '',
      contactNumber: normalized?.contactNumber || '',
      email: normalized?.email || '',
      trnNumber: normalized?.trnNumber || '',
      // type is immutable; loaded for reference (the selector is not shown when editing). Fall back to
      // the parent-derived effective type for rows created before the type field existed.
      type: normalized?.type || (isB2b ? 'TYPING_CENTER' : 'CLIENT')
    });
    setPortalOverrides(portalFeaturesService.overridesFor(normalized));
    resetLicence();
    if ((normalized?.type || (isB2b ? 'TYPING_CENTER' : 'CLIENT')) === 'CLIENT' && normalized?.id) {
      tradeLicenceService.get(normalized.id).then((data) => {
        if (data) {
          setLicence(toLicenceForm(data));
          setLicenceOpen(true);
        }
      }).catch(() => {});
    }
    setOpenInternalOrgDialog(true);
  };

  const handleSaveInternalOrg = async () => {
    const centerId = resolvedTypingCenterId;
    if (!centerId) return;

    try {
      setInternalOrgSaving(true);
      setInternalOrgsError(null);

      const payload = {
        typing_center_id: centerId,
        name: internalOrgFormData.name,
        address: internalOrgFormData.address,
        emirate: internalOrgFormData.emirate,
        country: internalOrgFormData.country,
        contact_person: internalOrgFormData.contactPerson,
        contact_number: internalOrgFormData.contactNumber,
        email: internalOrgFormData.email,
        trn_number: internalOrgFormData.trnNumber,
        // Customer type is set by the tab the user created from; the backend still validates it
        // against the parent TC. Immutable, so it is only sent on create, not on edit.
        ...(!editingInternalOrg ? { type: internalOrgFormData.type } : {})
      };

      const initialPortalOverrides = editingInternalOrg ? portalFeaturesService.overridesFor(editingInternalOrg) : {};
      const isClient = internalOrgFormData.type === 'CLIENT';
      const result = await saveClientWithLicence({
        saveClient: async () => {
          const res = editingInternalOrg?.id
            ? await ApiService.put(`/api/internal-organizations/${editingInternalOrg.id}`, payload)
            : await ApiService.post('/api/internal-organizations', payload);
          return { ...(editingInternalOrg || {}), ...(res?.data?.data || {}) };
        },
        saveDocument: (id) => tcOrgDocumentApi(id).upload([licenceFile.current], 'TRADE_LICENSE', {
          documentNumber: licence.licenceNumber || undefined,
          issueDate: licence.issueDate || undefined,
          expiryDate: licence.expiryDate || undefined,
          metadata: licenceMetadata(licence),
        }),
        saveLicence: (id, body) => tradeLicenceService.save(id, body),
        licence,
        pending: isClient ? licencePending.current : { document: false, profile: false },
      });
      licencePending.current = result.pending;
      if (result.error) {
        if (result.savedClient) {
          setEditingInternalOrg(result.savedClient);
          await fetchInternalOrganizations();
        }
        setClientDialogError(result.error);
        return;
      }
      const savedClient = result.savedClient;

      if (savedClient?.id && JSON.stringify(portalOverrides) !== JSON.stringify(initialPortalOverrides)) {
        await portalFeaturesService.saveOverrides(savedClient.id, portalOverrides);
      }
      setOpenInternalOrgDialog(false);
      setEditingInternalOrg(null);
      await fetchInternalOrganizations();
    } catch (err) {
      console.error('❌ [InternalOrganizations] Error saving internal organization:', err);
      setInternalOrgsError(err.response?.data?.error || err.response?.data?.message || `Failed to save ${clientTerm.toLowerCase()}`);
    } finally {
      setInternalOrgSaving(false);
    }
  };

  const handleOpenDeleteDialog = async (org) => {
    if (!org?.id) return;
    setDeleteDialog({
      open: true, org, loading: true, submitting: false,
      walletBalance: 0, banks: [], refund: false, bankId: '', error: null
    });
    try {
      const [walletRes, banksRes] = await Promise.allSettled([
        ApiService.getCustomerWalletByOrg(org.id),
        ApiService.getTcBankAccounts(resolvedTypingCenterId)
      ]);
      let balance = 0;
      if (walletRes.status === 'fulfilled') {
        const w = walletRes.value?.data?.data || {};
        balance = Number(w.approved_balance ?? w.approvedBalance ?? 0);
      }
      let banks = [];
      if (banksRes.status === 'fulfilled') {
        banks = (banksRes.value?.data?.data || []).map((b) => ({
          id: b.id,
          name: b.bank_name || b.bankName || b.account_name || b.accountName || 'Bank',
          balance: Number(b.balance ?? 0)
        }));
      }
      setDeleteDialog((d) => ({ ...d, loading: false, walletBalance: balance, banks }));
    } catch (e) {
      setDeleteDialog((d) => ({ ...d, loading: false, error: 'Failed to load wallet / bank details' }));
    }
  };

  const handleCloseDeleteDialog = () => {
    if (deleteDialog.submitting) return;
    setDeleteDialog((d) => ({ ...d, open: false }));
  };

  const handleConfirmDelete = async () => {
    const { org, refund, bankId } = deleteDialog;
    if (!org?.id) return;
    setDeleteDialog((d) => ({ ...d, submitting: true, error: null }));
    try {
      const qs = refund && bankId ? `?refundBankAccountId=${bankId}` : '';
      await ApiService.delete(`/api/internal-organizations/${org.id}${qs}`);
      setDeleteDialog((d) => ({ ...d, open: false, submitting: false }));
      await fetchInternalOrganizations();
    } catch (err) {
      setDeleteDialog((d) => ({
        ...d, submitting: false,
        error: err.response?.data?.error || err.response?.data?.message || `Failed to remove ${clientTerm.toLowerCase()}`
      }));
    }
  };

  const fmtAed = (x) => Number(x || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtMoney = (x) => (x == null ? '—' : `AED ${fmtAed(x)}`);
  const netLiabilityOf = (fin) => (fin?.outstanding == null || fin?.walletBalance == null ? null : fin.outstanding - fin.walletBalance);
  // Employee count as a small filled circle beside the name; CLIENT rows only.
  const employeeBadge = (orgId) => {
    const n = employeeCounts[orgId];
    if (n === undefined) return null;
    return (
      <Tooltip title={`${n} employee${n === 1 ? '' : 's'}`}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            minWidth: 14, height: 14, px: 0.4, borderRadius: 7,
            bgcolor: 'action.selected', color: 'text.secondary',
            fontSize: '0.6rem', fontWeight: 500, lineHeight: 1
          }}
        >
          {n}
        </Box>
      </Tooltip>
    );
  };

  const fmtInvoiceCounts = (fin) => (fin?.finalCount == null ? '—' : `${fin.finalCount} Final | ${fin.draftCount || 0} Draft`);
  const deleteSelectedBank = deleteDialog.banks.find((b) => b.id === deleteDialog.bankId) || null;
  const deleteInsufficient = deleteDialog.refund && !!deleteSelectedBank
    && deleteSelectedBank.balance < deleteDialog.walletBalance;
  const deleteConfirmDisabled = deleteDialog.submitting || deleteDialog.loading
    || (deleteDialog.refund && (!deleteDialog.bankId || deleteInsufficient));

  // Fetch ticket overview data for each organization
  useEffect(() => {
    const fetchTicketData = async () => {
      if (organizations.length === 0) return;

      try {
        console.log('🎫 [Organizations] Fetching ticket data for', organizations.length, 'organizations');

        // Fetch ticket data for each organization individually
        const ticketDataPromises = organizations.map(async (org) => {
          const orgId = org.organizationId || org.id;
          try {
            const response = await ApiService.getTicketOverview([orgId]);
            return {
              orgId,
              data: response.data || {}
            };
          } catch (err) {
            console.warn(`⚠️ [Organizations] Error fetching ticket data for org ${orgId}:`, err.message);
            return {
              orgId,
              data: {
                paidTicketPaymentsAmount: 0,
                paidTicketCount: 0,
                pendingTicketPaymentsAmount: 0,
                pendingTicketCount: 0
              }
            };
          }
        });

        const results = await Promise.all(ticketDataPromises);

        // Convert array to object map
        const dataMap = {};
        results.forEach(({ orgId, data }) => {
          dataMap[orgId] = data;
        });

        setOrgTicketData(dataMap);
        console.log('✅ [Organizations] Ticket data fetched for all organizations');
      } catch (err) {
        console.error('❌ [Organizations] Error fetching ticket data:', err);
      }
    };

    fetchTicketData();
  }, [organizations]);

  // Fetch wallet balance data for each organization
  useEffect(() => {
    const fetchWalletData = async () => {
      if (organizations.length === 0 || !canReadFinance) return;

      try {
        console.log('💰 [Organizations] Fetching wallet data for', organizations.length, 'organizations');

        // Fetch wallet data for each organization individually
        const walletDataPromises = organizations.map(async (org) => {
          const orgId = org.organizationId || org.id;
          try {
            const response = await ApiService.getWalletBalance(orgId);
            return {
              orgId,
              data: response.data?.data || {}
            };
          } catch (err) {
            console.warn(`⚠️ [Organizations] Error fetching wallet data for org ${orgId}:`, err.message);
            return {
              orgId,
              data: null
            };
          }
        });

        const results = await Promise.all(walletDataPromises);

        // Convert array to object map
        const dataMap = {};
        results.forEach(({ orgId, data }) => {
          dataMap[orgId] = data;
        });

        setOrgWalletData(dataMap);
        console.log('✅ [Organizations] Wallet data fetched for all organizations:', dataMap);
      } catch (err) {
        console.error('❌ [Organizations] Error fetching wallet data:', err);
      }
    };

    fetchWalletData();
  }, [organizations, canReadFinance]);

  // Get typing center name for display
  const getTypingCenterName = () => {
    if (isPlatform) {
      const center = typingCenters.find(tc => tc.id === selectedTypingCenter);
      return center ? `${center.name} (${center.code})` : 'All Centers';
    }
    return currentUser?.typingCenterName || '';
  };

  // Thaklis organizations not yet mapped to the selected typing center
  const getUnmappedOrganizations = () => {
    if (!resolvedTypingCenterId) return [];
    const mappedOrgIds = new Set(organizations.map(m => m.organizationId || m.id));
    return thaklisOrganizations.filter(org => !mappedOrgIds.has(org.id) && !takenOrgIds.has(org.id));
  };

  const unmappedOrganizations = getUnmappedOrganizations();

  // Handle opening config dialog
  const handleOpenConfig = async (org, event) => {
    event.stopPropagation(); // Prevent card click navigation
    setConfigOrg(org);
    setOpenConfigDialog(true);
    setConfigLoading(true);
    setDefaultEmailsError('');
    setConfigTab(0);
    // Reset credentials state
    setCredentialsForm({ thaklisUsername: '', thaklisPassword: '' });
    setCredentialStatus(null);
    setShowPassword(false);

    const mappingId = org.mappingId || org.id;

    // Credentials are a Thaklis feature — skip that request entirely when it is off.
    try {
      const [configResponse, credentialResponse] = await Promise.all([
        ApiService.getOrganizationConfig(mappingId).catch(err => {
          console.error('Error loading org config:', err);
          return null;
        }),
        thaklisServicesOff ? Promise.resolve(null) : ApiService.getOrganizationCredentialStatus(mappingId).catch(err => {
          console.error('Error loading credential status:', err);
          return null;
        })
      ]);

      if (configResponse?.data?.success) {
        const config = configResponse.data.data;
        const incomingDefaultEmails = config.default_emails ?? config.defaultEmails ?? '';
        if (Array.isArray(incomingDefaultEmails)) {
          setDefaultEmails(incomingDefaultEmails.join(', '));
        } else {
          setDefaultEmails(String(incomingDefaultEmails || ''));
        }
        setConfigNotes(config.notes || '');
      } else {
        setDefaultEmails('');
        setConfigNotes('');
      }

      if (credentialResponse?.data?.success) {
        setCredentialStatus(credentialResponse.data.data);
      }
    } catch (err) {
      console.error('Error loading config:', err);
      setDefaultEmails('');
      setConfigNotes('');
    } finally {
      setConfigLoading(false);
    }
  };

  // Handle closing config dialog
  const handleCloseConfig = () => {
    setOpenConfigDialog(false);
    setConfigClosedCount((n) => n + 1);
    setConfigOrg(null);
    setDefaultEmails('');
    setDefaultEmailsError('');
    setConfigNotes('');
    setConfigTab(0);
    // Reset credentials state
    setCredentialStatus(null);
    setCredentialsForm({ thaklisUsername: '', thaklisPassword: '' });
    setShowPassword(false);
  };

  const validateDefaultEmails = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const parts = trimmed
      .split(/[;,\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);

    const invalid = parts.find((email) => !emailRegex.test(email));
    return invalid ? 'Please enter valid email address(es)' : '';
  };

  // Handle saving config
  const handleSaveConfig = async () => {
    if (!configOrg) return;

    const validationError = validateDefaultEmails(defaultEmails);
    if (validationError) {
      setDefaultEmailsError(validationError);
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }

    setConfigSaving(true);
    try {
      const mappingId = configOrg.mappingId || configOrg.id;
      await ApiService.updateOrganizationConfig(mappingId, {
        defaultEmails: String(defaultEmails || ''),
        notes: configNotes
      });

      setSnackbar({ open: true, message: 'Organization settings saved successfully', severity: 'success' });
      handleCloseConfig();
    } catch (err) {
      console.error('Error saving org config:', err);
      setSnackbar({ open: true, message: 'Failed to save organization settings', severity: 'error' });
    } finally {
      setConfigSaving(false);
    }
  };

  // Credentials Handlers
  const handleSaveCredentials = async () => {
    if (!configOrg) return;
    if (!credentialsForm.thaklisUsername || !credentialsForm.thaklisPassword) return;

    setCredentialsSaving(true);
    try {
      const mappingId = configOrg.mappingId || configOrg.id;
      const response = await ApiService.setOrganizationCredentials(
        mappingId,
        credentialsForm
      );
      if (response.data && response.data.success) {
        setCredentialStatus(response.data.data);
        setCredentialsForm({ thaklisUsername: '', thaklisPassword: '' });
        setSnackbar({ open: true, message: response.data.message || 'Credentials saved successfully', severity: 'success' });
      }
    } catch (err) {
      console.error('Error saving credentials:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to save credentials', severity: 'error' });
    } finally {
      setCredentialsSaving(false);
    }
  };

  const handleTestCredentials = async () => {
    if (!configOrg) return;

    setCredentialsTesting(true);
    try {
      const mappingId = configOrg.mappingId || configOrg.id;
      const response = await ApiService.testOrganizationCredentials(mappingId);
      if (response.data && response.data.success) {
        setCredentialStatus(response.data.data);
        setSnackbar({ open: true, message: response.data.message || 'Connection test completed', severity: 'success' });
      }
    } catch (err) {
      console.error('Error testing credentials:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to test credentials', severity: 'error' });
    } finally {
      setCredentialsTesting(false);
    }
  };

  const handleRemoveCredentials = async () => {
    if (!configOrg) return;
    if (!window.confirm('Are you sure you want to remove the stored credentials?')) return;

    setCredentialsSaving(true);
    try {
      const mappingId = configOrg.mappingId || configOrg.id;
      const response = await ApiService.deleteOrganizationCredentials(mappingId);
      if (response.data && response.data.success) {
        setCredentialStatus(response.data.data);
        setSnackbar({ open: true, message: 'Credentials removed successfully', severity: 'success' });
      }
    } catch (err) {
      console.error('Error removing credentials:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to remove credentials', severity: 'error' });
    } finally {
      setCredentialsSaving(false);
    }
  };

  // Handle mapping organizations
  const handleMapOrganizations = async () => {
    const centerId = resolvedTypingCenterId;
    if (!centerId) return;
    setMappingSaving(true);
    try {
      for (const orgId of selectedOrganizations) {
        await ApiService.post('/api/mappings', { typingCenterId: centerId, organizationId: orgId });
      }
      setSelectedOrganizations([]);
      setOpenMappingDialog(false);
      setSnackbar({ open: true, message: 'Organizations mapped successfully', severity: 'success' });
    } catch (err) {
      console.error('❌ [Organizations] Error mapping organizations:', err);
      setSnackbar({ open: true, message: serverMessage(err, 'Failed to map organizations'), severity: 'error' });
    } finally {
      setMappingSaving(false);
      await fetchMappedOrganizations();
      loadTakenOrgIds();
    }
  };

  // Render Internal Organizations Tab
  const renderInternalOrgsTab = (typeKey, term, termPlural) => {
    const rows = internalOrganizations;
    // Bulk portal access needs write; checkboxes only in the table view, and only after "Select clients" is turned on.
    const canSelect = can(currentUser, 'customer_organization', 'write');
    const canBulk = canSelect && selectMode && customerView === 'table';
    const pageIds = rows.map((o) => o.id);
    const allOnPage = pageIds.length > 0 && pageIds.every((id) => selectedClientIds.includes(id));
    const toggleId = (id) => setSelectedClientIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    void portalVersion; // re-render after a bulk change so the Portal column re-reads overrides
    const customerPaginationNode = customerTotal > 0 ? (
      <TablePagination
        component="div"
        count={customerTotal}
        page={customerPage}
        onPageChange={(e, newPage) => setCustomerPage(newPage)}
        rowsPerPage={customerRowsPerPage}
        onRowsPerPageChange={(e) => {
          setCustomerRowsPerPage(parseInt(e.target.value, 10));
          setCustomerPage(0);
        }}
        rowsPerPageOptions={[12, 24, 48]}
        labelRowsPerPage={`${termPlural} per page`}
      />
    ) : null;
    return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={`Search ${termPlural.toLowerCase()} by name or TRN...`}
          sx={{ flex: '1 1 200px', minWidth: 200, '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', flexShrink: 0 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={customerView}
            sx={{ height: 32 }}
            onChange={(e, next) => {
              if (!next) return;
              setCustomerView(next);
              if (next !== 'table') exitSelectMode();
              try { localStorage.setItem('organizationsCustomerView', next); } catch (err) { /* private mode */ }
            }}
          >
            <ToggleButton value="cards" aria-label="Card view">
              <Tooltip title="Card view"><CardViewIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table view"><TableViewIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {customerView === 'table' && (
            <ProTablePreferences columns={CLIENT_COLUMNS} preferences={clientColPrefs} />
          )}
          {canSelect && customerView === 'table' && (
            <Tooltip title={selectMode ? 'Stop selecting' : `Select ${termPlural.toLowerCase()} to change portal access`}>
              <IconButton
                size="small"
                onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                aria-label="Select clients for portal access"
                aria-pressed={selectMode}
                sx={{
                  border: '1px solid', borderColor: selectMode ? 'primary.main' : 'divider', borderRadius: 1,
                  ...(selectMode && { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } })
                }}
              >
                <SelectModeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {can(currentUser, 'customer_organization', 'write') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => handleOpenCreateInternalOrg(typeKey)}
              disabled={internalOrgsLoading || !resolvedTypingCenterId}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', whiteSpace: 'nowrap', height: 32, ml: 0.5 }}
            >
              Add {term}
            </Button>
          )}
        </Box>
      </Box>

      {internalOrgsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInternalOrgsError(null)}>
          {internalOrgsError}
        </Alert>
      )}

      {canBulk && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 1.5, py: 0.5, borderRadius: 1, bgcolor: 'action.selected', minHeight: 36 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
            {selectedClientIds.length > 0
              ? `${selectedClientIds.length} selected`
              : `Tick the ${termPlural.toLowerCase()} whose portal access you want to change`}
          </Typography>
          {selectedClientIds.length > 0 && (
            <Button size="small" variant="contained" onClick={() => setBulkPortal({ feature: PORTAL_FEATURES[0].key, value: OVERRIDE.OFF, saving: false, error: null })}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', height: 28 }}>
              Portal access…
            </Button>
          )}
          <Button size="small" onClick={exitSelectMode} sx={{ textTransform: 'none', fontSize: '0.8125rem', ml: 'auto' }}>
            Cancel
          </Button>
        </Box>
      )}

      {internalOrgsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {searchTerm ? `No ${termPlural.toLowerCase()} match "${searchTerm}"` : `No ${termPlural.toLowerCase()}`}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {searchTerm ? 'Try a different name or TRN.' : `Click "Add ${term}" to create one.`}
            </Typography>
          </CardContent>
        </Card>
      ) : customerView === 'table' ? (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  {canBulk && (
                    <TableCell padding="checkbox" sx={{ '& .MuiCheckbox-root': { color: 'inherit' } }}>
                      <Checkbox
                        size="small"
                        checked={allOnPage}
                        indeterminate={!allOnPage && pageIds.some((id) => selectedClientIds.includes(id))}
                        onChange={() => setSelectedClientIds((prev) => (allOnPage
                          ? prev.filter((id) => !pageIds.includes(id))
                          : Array.from(new Set([...prev, ...pageIds]))))}
                        inputProps={{ 'aria-label': 'Select all on this page' }}
                      />
                    </TableCell>
                  )}
                  {clientColPrefs.visibleColumns.map((col) => (
                    <TableCell key={col.key} align={col.align} sx={{ fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{col.label}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((internalOrg) => {
                  const fin = orgFinancials[internalOrg.id] || {};
                  const netLiability = netLiabilityOf(fin);
                  return (
                    <TableRow
                      key={internalOrg.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/organizations/internal/${internalOrg.id}`, { state: { org: internalOrg } })}
                    >
                      {canBulk && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={selectedClientIds.includes(internalOrg.id)}
                            onChange={() => toggleId(internalOrg.id)}
                            inputProps={{ 'aria-label': `Select ${internalOrg.name}` }}
                          />
                        </TableCell>
                      )}
                      {clientColPrefs.visibleColumns.map((col) => {
                        switch (col.key) {
                          case 'name':
                            return (
                              <TableCell key={col.key} sx={{ py: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                                    {internalOrg.name}
                                  </Typography>
                                  {employeeBadge(internalOrg.id)}
                                </Box>
                                {internalOrg.email && (
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                    {internalOrg.email}
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          case 'trn':
                            return <TableCell key={col.key} sx={{ fontSize: '0.8125rem' }}>{internalOrg.trnNumber || '—'}</TableCell>;
                          case 'outstanding':
                            return (
                              <TableCell key={col.key} align="right" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: (fin.outstanding || 0) > 0 ? '#FF9830' : 'text.primary' }}>
                                {fmtMoney(fin.outstanding)}
                              </TableCell>
                            );
                          case 'walletBalance':
                            return (
                              <TableCell key={col.key} align="right" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                                {fmtMoney(fin.walletBalance)}
                              </TableCell>
                            );
                          case 'netLiability':
                            return (
                              <TableCell key={col.key} align="right" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: netLiability == null ? 'text.primary' : netLiability > 0 ? '#ff5722' : '#73BF69' }}>
                                {fmtMoney(netLiability)}
                              </TableCell>
                            );
                          case 'invoices':
                            return (
                              <TableCell key={col.key} align="center" sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {fmtInvoiceCounts(fin)}
                              </TableCell>
                            );
                          case 'portal': {
                            const summary = overridesSummary(portalFeaturesService.overridesFor(internalOrg), portalDefaults);
                            return (
                              <TableCell key={col.key} sx={{ fontSize: '0.75rem', color: summary.custom ? 'text.primary' : 'text.secondary', maxWidth: 220 }}>
                                {summary.custom ? (
                                  <Tooltip title={summary.text}>
                                    <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      Custom · {summary.text}
                                    </Box>
                                  </Tooltip>
                                ) : 'Default'}
                              </TableCell>
                            );
                          }
                          default:
                            return null;
                        }
                      })}
                      {/* Action buttons do their own thing; keep their clicks from also opening the row. */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 0.25 }} onClick={(e) => e.stopPropagation()}>
                        {can(currentUser, 'customer_organization', 'write') && (
                          <Tooltip title="Update">
                            <IconButton size="small" onClick={() => handleOpenEditInternalOrg(internalOrg)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {can(currentUser, 'customer_organization', 'delete') && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(internalOrg)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Manage Portal Users">
                          <IconButton size="small" color="primary" onClick={() => handleOpenUsersDialog(internalOrg)}>
                            <PeopleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/organizations/internal/${internalOrg.id}`, { state: { org: internalOrg } })}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {customerPaginationNode}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {rows.map((internalOrg) => (
            <Grid item xs={12} sm={6} md={4} key={internalOrg.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 16px rgba(0,0,0,0.08)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1 }}>
                      <BusinessIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }} noWrap>
                            {internalOrg.name}
                          </Typography>
                          {employeeBadge(internalOrg.id)}
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }} noWrap>
                          {term}
                        </Typography>
                        {internalOrg.trnNumber && (
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }} noWrap>
                            TRN: {internalOrg.trnNumber}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {can(currentUser, 'customer_organization', 'write') && (
                        <Tooltip title="Update">
                          <IconButton size="small" onClick={() => handleOpenEditInternalOrg(internalOrg)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {can(currentUser, 'customer_organization', 'delete') && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(internalOrg)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Manage Portal Users">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenUsersDialog(internalOrg)}
                        >
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Financial Summary */}
                  <Box sx={{ mb: 1.5 }}>
                    {(() => {
                      const fin = orgFinancials[internalOrg.id];
                      const net = netLiabilityOf(fin);
                      return [
                        { label: 'Outstanding', value: fin?.outstanding, color: fin?.outstanding > 0 ? '#FF9830' : undefined },
                        { label: 'Wallet Balance', value: fin?.walletBalance },
                        { label: 'Net Liability', value: net, color: net == null ? undefined : net > 0 ? '#ff5722' : '#73BF69' },
                      ];
                    })().map((row) => (
                      <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: row.color || 'text.primary' }}>
                          {fmtMoney(row.value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Invoice counts + View Details */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {fmtInvoiceCounts(orgFinancials[internalOrg.id])}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => navigate(`/organizations/internal/${internalOrg.id}`, { state: { org: internalOrg } })}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                    >
                      View Details →
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {customerView !== 'table' && customerPaginationNode}
    </>
    );
  };

  return (
    <Box>
      {isPlatform && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isPlatform && (
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <Select
                value={selectedTypingCenter}
                onChange={(e) => setSelectedTypingCenter(e.target.value)}
                displayEmpty
                sx={{ bgcolor: 'background.paper', fontSize: '0.875rem' }}
              >
                {typingCenters.map(center => (
                  <MenuItem key={center.id} value={center.id} sx={{ fontSize: '0.875rem' }}>
                    {center.name} ({center.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        {isPlatform && activeTabKey === 'company' && can(currentUser, 'customer_organization', 'write') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedOrganizations([]);
                setOpenMappingDialog(true);
              }}
              disabled={unmappedOrganizations.length === 0}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            >
              Add Organization
            </Button>
        )}
      </Box>
      )}

      {/* Main Tabs */}
      <Tabs
        value={activeTabKey}
        onChange={(e, newValue) => {
          setSelectedTabKey(newValue);
          const next = new URLSearchParams(urlParams);
          next.set('tab', newValue);
          next.delete('view');
          setUrlParams(next, { replace: true });
        }}
        sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="company"
          label="Our Company"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 }}
        />
        {customerTabs.map((typeKey) => (
          <Tab
            key={typeKey}
            value={typeKey}
            label={`${termPluralFor(typeKey)} (${customerCounts[typeKey] ?? 0})`}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 }}
          />
        ))}
        {showCustomerGroupsTab && (
          <Tab
            value="groups"
            label="Customer Groups"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 }}
          />
        )}
      </Tabs>

      {/* Tab Content */}
      {activeTabKey === 'company' && (
        <OurCompanyTab
          currentUser={currentUser}
          typingCenterId={resolvedTypingCenterId}
          isPlatform={isPlatform}
          thaklisServicesOff={thaklisServicesOff}
          accounts={organizations}
          accountsLoading={loading}
          walletDataByOrg={orgWalletData}
          ticketDataByOrg={orgTicketData}
          canReadFinance={canReadFinance}
          fmtMoney={fmtMoney}
          onOpenSettings={handleOpenConfig}
          statusRefreshKey={configClosedCount}
          view={companyView}
          onViewChange={setCompanyView}
        />
      )}
      {activeTabKey === 'groups' && (
        <PortalCustomerGroups currentUser={currentUser} embedded />
      )}
      {activeCustomerType && renderInternalOrgsTab(
        activeCustomerType,
        termFor(activeCustomerType),
        termPluralFor(activeCustomerType)
      )}

      {/* Map Organizations Dialog */}
      <Dialog open={openMappingDialog} onClose={() => setOpenMappingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Organizations to {getTypingCenterName()}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
              Available Organizations from Thaklis ({unmappedOrganizations.length})
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {unmappedOrganizations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    Every organization is already mapped to a typing center
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {unmappedOrganizations.map((org) => (
                    <ListItem key={org.id}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedOrganizations.includes(org.id)}
                          onChange={() => {
                            setSelectedOrganizations(prev =>
                              prev.includes(org.id)
                                ? prev.filter(id => id !== org.id)
                                : [...prev, org.id]
                            );
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{org.name}</Typography>}
                        secondary={<Typography sx={{ fontSize: '0.75rem' }}>{org.code}</Typography>}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
            {selectedOrganizations.length > 0 && (
              <Typography variant="body2" sx={{ mt: 2, fontSize: '0.875rem', color: 'secondary.main' }}>
                {selectedOrganizations.length} organization(s) selected
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenMappingDialog(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleMapOrganizations}
            variant="contained"
            disabled={selectedOrganizations.length === 0 || mappingSaving}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            Add ({selectedOrganizations.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openInternalOrgDialog} onClose={() => !internalOrgSaving && setOpenInternalOrgDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {editingInternalOrg ? `Update ${termFor(internalOrgFormData.type)}` : `Add ${termFor(internalOrgFormData.type)}`}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="fieldset" disabled={internalOrgSaving} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          {clientDialogError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem' }} onClose={() => setClientDialogError(null)}>{clientDialogError}</Alert>
          )}
          {internalOrgFormData.type === 'CLIENT' && (
            <>
              <TradeLicenceImport
                {...licenceImport}
                onImport={handleImportLicence}
                onDismissError={() => setLicenceImport((p) => ({ ...p, error: null }))}
              />
              {Object.keys(licenceSuggestions).length > 0 && (
                <Alert severity="info" icon={false} sx={{ mb: 2, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem', width: '100%' } }}>
                  {Object.entries(licenceSuggestions).map(([field, value]) => (
                    <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        {SUGGESTION_LABELS[field]} on the licence: <strong>{value}</strong>
                      </Box>
                      <Button size="small" sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        onClick={() => {
                          setInternalOrgFormData((p) => ({ ...p, [field]: value }));
                          setLicenceSuggestions(({ [field]: _used, ...rest }) => rest);
                        }}>
                        Use
                      </Button>
                    </Box>
                  ))}
                </Alert>
              )}
            </>
          )}
          <Box>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={internalOrgFormData.name}
                  onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, name: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
                  inputProps={{ sx: { fontSize: '0.8125rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="TRN"
                  value={internalOrgFormData.trnNumber}
                  onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, trnNumber: e.target.value })}
                  size="small"
                  placeholder="100XXXXXXXXX"
                  InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
                  inputProps={{ sx: { fontSize: '0.8125rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={internalOrgFormData.contactPerson}
                  onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, contactPerson: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
                  inputProps={{ sx: { fontSize: '0.8125rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  value={internalOrgFormData.contactNumber}
                  onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, contactNumber: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
                  inputProps={{ sx: { fontSize: '0.8125rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={internalOrgFormData.email}
                  onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, email: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
                  inputProps={{ sx: { fontSize: '0.8125rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8125rem' }}>Emirate</InputLabel>
                  <Select
                    label="Emirate"
                    value={internalOrgFormData.emirate}
                    onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, emirate: e.target.value })}
                    sx={{ fontSize: '0.8125rem' }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {emirateOptions.map((e) => (
                      <MenuItem key={e} value={e}>{e}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Address"
                  value={internalOrgFormData.address}
                  onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, address: e.target.value })}
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={3}
                  InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
                  inputProps={{ sx: { fontSize: '0.8125rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Country"
                  value={internalOrgFormData.country}
                  onChange={(e) => setInternalOrgFormData({ ...internalOrgFormData, country: e.target.value })}
                  size="small"
                  placeholder="UAE"
                  InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
                  inputProps={{ sx: { fontSize: '0.8125rem' } }}
                />
              </Grid>
            </Grid>
            {internalOrgFormData.type === 'CLIENT' && (
              <TradeLicenceSection
                licence={licence}
                open={licenceOpen}
                onOpen={() => setLicenceOpen(true)}
                today={new Date().toISOString().slice(0, 10)}
                onChange={(next) => {
                  setLicence(next);
                  licencePending.current = { ...licencePending.current, profile: true };
                }}
              />
            )}
            {/* Employees + Document Expiry are client-only Thaklis features. */}
            <PortalAccessSection
              overrides={portalOverrides}
              onChange={setPortalOverrides}
              defaults={portalDefaults}
              showThaklisFeatures={!thaklisDisabled && internalOrgFormData.type === 'CLIENT'}
            />
          </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setOpenInternalOrgDialog(false)}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
            disabled={internalOrgSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveInternalOrg}
            variant="contained"
            disabled={!internalOrgFormData.name || internalOrgSaving}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {internalOrgSaving ? 'Saving...' : editingInternalOrg ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove client (with optional refund of the wallet balance to the customer) */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Remove {deleteDialog.org ? termFor(effectiveOrgType(deleteDialog.org)) : clientTerm}</DialogTitle>
        <DialogContent>
          {deleteDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <b>{deleteDialog.org?.name}</b> will be hidden from the {clientTermPlural.toLowerCase()} list and pickers,
                its wallet hidden, and its portal login disabled. Existing invoices and work orders are kept.
              </Typography>

              {deleteDialog.walletBalance > 0 && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This {clientTerm.toLowerCase()}'s wallet holds <b>AED {fmtAed(deleteDialog.walletBalance)}</b>.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deleteDialog.refund}
                        onChange={(e) => setDeleteDialog((d) => ({ ...d, refund: e.target.checked, error: null }))}
                      />
                    }
                    label="Refund the wallet balance to the customer now"
                  />
                  {deleteDialog.refund ? (
                    <Box sx={{ mt: 1 }}>
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>Refund from bank account</InputLabel>
                        <Select
                          label="Refund from bank account"
                          value={deleteDialog.bankId}
                          onChange={(e) => setDeleteDialog((d) => ({ ...d, bankId: e.target.value }))}
                        >
                          {deleteDialog.banks.map((b) => (
                            <MenuItem key={b.id} value={b.id}>{b.name} - AED {fmtAed(b.balance)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        The wallet balance (AED {fmtAed(deleteDialog.walletBalance)}) will be returned to the customer:
                        the wallet is set to zero and AED {fmtAed(deleteDialog.walletBalance)} is debited from the
                        selected bank account.
                      </Typography>
                      {deleteInsufficient && (
                        <Alert severity="error" sx={{ mt: 1 }}>Selected bank account balance is insufficient</Alert>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      You can refund this balance to the customer later from the Wallets page.
                    </Typography>
                  )}
                </Box>
              )}

              {deleteDialog.error && <Alert severity="error" sx={{ mt: 2 }}>{deleteDialog.error}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteDialog.submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteConfirmDisabled}>
            {deleteDialog.submitting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Organization Config Dialog */}
      <Dialog open={openConfigDialog} onClose={handleCloseConfig} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Configure {configOrg?.organizationName || configOrg?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {configLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {!thaklisServicesOff && (
                <Tabs
                  value={configTab}
                  onChange={(e, newValue) => setConfigTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                  <Tab
                    label="Email & Notes"
                    icon={<EmailIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: '0.875rem', minHeight: 48 }}
                  />
                  <Tab
                    label="Credentials"
                    icon={<CredentialsIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: '0.875rem', minHeight: 48 }}
                  />
                </Tabs>
              )}

              {/* Tab 0: Email & Notes */}
              {configTab === 0 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="primary" />
                    Default Email Addresses
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>
                    These emails will be pre-filled when sending invoices to this organization
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Enter email address(es)"
                    value={defaultEmails}
                    onChange={(e) => {
                      const next = e.target.value;
                      setDefaultEmails(next);
                      setDefaultEmailsError(validateDefaultEmails(next));
                    }}
                    error={Boolean(defaultEmailsError)}
                    helperText={defaultEmailsError}
                    sx={{ mb: 3, '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                  />

                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Notes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add notes about this organization..."
                    value={configNotes}
                    onChange={(e) => setConfigNotes(e.target.value)}
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                  />
                </Box>
              )}

              {/* Tab 1: Credentials */}
              {configTab === 1 && !thaklisServicesOff && (
                <Box sx={{ p: 3 }}>
                  {/* Status + Test Connection row */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                        Status
                      </Typography>
                      <Chip
                        label={
                          !credentialStatus?.hasCredentials
                            ? 'Not Configured'
                            : credentialStatus?.isConnected
                              ? 'Connected'
                              : 'Disconnected'
                        }
                        color={
                          !credentialStatus?.hasCredentials
                            ? 'default'
                            : credentialStatus?.isConnected
                              ? 'success'
                              : 'error'
                        }
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Box>
                    {credentialStatus?.hasCredentials && (
                      <Button
                        onClick={handleTestCredentials}
                        variant="text"
                        size="small"
                        color="primary"
                        disabled={credentialsTesting || credentialsSaving}
                        sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                      >
                        {credentialsTesting ? 'Testing...' : 'Test Connection'}
                      </Button>
                    )}
                  </Box>

                  {/* Credential Form */}
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>
                    {credentialStatus?.hasCredentials
                      ? 'Enter new credentials to update. Leave empty to keep existing.'
                      : 'Enter the Thaklis credentials for this organization.'}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Username"
                    value={credentialsForm.thaklisUsername}
                    onChange={(e) => setCredentialsForm({ ...credentialsForm, thaklisUsername: e.target.value })}
                    size="small"
                    sx={{ mb: 2 }}
                    InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                    inputProps={{ sx: { fontSize: '0.875rem' } }}
                    autoComplete="off"
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={credentialsForm.thaklisPassword}
                    onChange={(e) => setCredentialsForm({ ...credentialsForm, thaklisPassword: e.target.value })}
                    size="small"
                    InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                    inputProps={{ sx: { fontSize: '0.875rem' } }}
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  {/* Remove Credentials Button */}
                  {credentialStatus?.hasCredentials && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button
                        onClick={handleRemoveCredentials}
                        color="error"
                        size="small"
                        disabled={credentialsSaving || credentialsTesting}
                        sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                      >
                        Remove Credentials
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfig} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          {configTab === 0 || thaklisServicesOff ? (
            <Button
              onClick={handleSaveConfig}
              variant="contained"
              disabled={configLoading || configSaving}
              sx={{ textTransform: 'none' }}
            >
              {configSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          ) : (
            <Button
              onClick={handleSaveCredentials}
              variant="contained"
              disabled={!credentialsForm.thaklisUsername || !credentialsForm.thaklisPassword || credentialsSaving}
              sx={{ textTransform: 'none' }}
            >
              {credentialsSaving ? 'Saving...' : 'Save Credentials'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Portal Users Dialog */}
      <InternalOrgUsersDialog
        open={openUsersDialog}
        onClose={handleCloseUsersDialog}
        organization={selectedOrgForUsers}
      />

      {/* Bulk portal access (14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §4.3, task PF4) */}
      <Dialog open={!!bulkPortal} onClose={() => !bulkPortal?.saving && setBulkPortal(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          Portal access · {selectedClientIds.length} {selectedClientIds.length === 1 ? 'client' : 'clients'}
        </DialogTitle>
        <DialogContent dividers>
          {bulkPortal?.error && <Alert severity="error" sx={{ mb: 1.5 }}>{bulkPortal.error}</Alert>}
          <FormControl fullWidth size="small" sx={{ mb: 2, '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: '0.8125rem' } }}>
            <InputLabel>Feature</InputLabel>
            <Select
              label="Feature"
              value={bulkPortal?.feature || ''}
              onChange={(e) => setBulkPortal((prev) => ({ ...prev, feature: e.target.value }))}
            >
              {PORTAL_FEATURES.filter((f) => !(f.thaklisOnly && thaklisDisabled)).map((f) => (
                <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.8125rem' }}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            exclusive
            size="small"
            fullWidth
            value={bulkPortal?.value || OVERRIDE.OFF}
            onChange={(e, v) => v && setBulkPortal((prev) => ({ ...prev, value: v }))}
            sx={{
              '& .MuiToggleButton-root': { height: 30, fontSize: '0.75rem', textTransform: 'none' },
              '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } },
            }}
          >
            <ToggleButton value={OVERRIDE.DEFAULT}>Default</ToggleButton>
            <ToggleButton value={OVERRIDE.ON}>On</ToggleButton>
            <ToggleButton value={OVERRIDE.OFF}>Off</ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
            Default makes these clients follow Settings › Client Portal again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setBulkPortal(null)} disabled={bulkPortal?.saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={bulkPortal?.saving}
            sx={{ textTransform: 'none' }}
            onClick={async () => {
              setBulkPortal((prev) => ({ ...prev, saving: true, error: null }));
              try {
                await portalFeaturesService.bulkSet(selectedClientIds, bulkPortal.feature, bulkPortal.value);
                setBulkPortal(null);
                exitSelectMode();
                setPortalVersion((v) => v + 1);
                await fetchInternalOrganizations();
              } catch (err) {
                setBulkPortal((prev) => ({ ...prev, saving: false, error: serverMessage(err, 'Failed to update portal access') }));
              }
            }}
          >
            {bulkPortal?.saving ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Organizations;
